import { Button as MuiButton, ButtonProps, alpha, darken } from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";
import { ArrowForward } from "@mui/icons-material";

export type ButtonTone =
  "primary" | "secondary" | "ghost" | "accent" | "outline" | "dark" | "hero" | "link" | "compact" | "navy" | "surface";

// `.btn` del rediseño v2 del prototipo: lo común de `navy` (`.btn-primary`) y `surface` (`.btn-secondary`).
const BTN_PROTOTIPO_SX = {
  height: 42,
  px: "18px",
  borderRadius: "8px",
  border: "1px solid",
  fontFamily: (theme: Theme) => theme.typography.fontFamily,
  fontSize: 14,
  fontWeight: 500,
  letterSpacing: "-0.005em",
  whiteSpace: "nowrap",
  transition:
    "background-color .15s ease, border-color .15s ease, box-shadow .2s ease, transform .12s ease, color .15s ease",
  "& .MuiButton-endIcon": { ml: 1 },
  "& .MuiButton-endIcon svg": { fontSize: 14 },
  "&:hover": { transform: "translateY(-1px)" },
  "&:active": { transform: "translateY(0) scale(.985)", transitionDuration: ".08s" },
};

interface Props extends Omit<ButtonProps, "color"> {
  tone?: ButtonTone;
  withArrow?: boolean;
}

const TONE_SX: Record<ButtonTone, SxProps<Theme>> = {
  primary: {
    bgcolor: "primary.main",
    color: "#fff",
    // Si la marca define `primary.hover` se usa tal cual; si no, se oscurece `main` con el filtro.
    "&:hover": (theme) =>
      theme.palette.primary.hover
        ? { bgcolor: theme.palette.primary.hover }
        : { bgcolor: theme.palette.primary.main, filter: "brightness(0.92)" },
    "&.Mui-disabled": { bgcolor: "primary.main", opacity: 0.4, color: "#fff" },
  },
  secondary: {
    bgcolor: "primary.light",
    color: "primary.dark",
    "&:hover": { bgcolor: "primary.base" },
  },
  ghost: {
    bgcolor: "transparent",
    color: "primary.dark",
    "&:hover": { bgcolor: "action.hover" },
  },
  accent: {
    bgcolor: "primary.accent",
    color: "#fff",
    "&:hover": { opacity: 0.9, bgcolor: "primary.accent" },
  },
  // Blanco con borde frío: el "Cancelar" de los modales del diseño, que necesita leerse como
  // botón (a diferencia de `ghost`, que es un texto sin contorno).
  outline: {
    bgcolor: "background.paper",
    color: "primary.dark",
    border: "1px solid #D5E1EC",
    "&:hover": { bgcolor: "#F7FAFD", borderColor: "#D5E1EC" },
    "&.Mui-disabled": { bgcolor: "background.paper", borderColor: "#D5E1EC", opacity: 0.5 },
  },
  // Navy: acción destacada pero neutral, como el "Sí, descartar" del diseño.
  dark: {
    bgcolor: "primary.dark",
    color: "#fff",
    "&:hover": { bgcolor: "primary.dark", filter: "brightness(1.25)" },
    "&.Mui-disabled": { bgcolor: "primary.dark", opacity: 0.4, color: "#fff" },
  },
  // CTA principal de los cotizadores v2 (`.btn-primary.btn-lg.quote-hero-cta`, antes `ButtonCotizador`):
  // 44px, radio 6 y sube 1px en hover. Usar con `withArrow`.
  hero: {
    height: 44,
    px: "20px",
    borderRadius: "6px",
    fontFamily: (theme) => theme.typography.fontFamily,
    fontSize: 14,
    fontWeight: 500,
    letterSpacing: "-0.005em",
    whiteSpace: "nowrap",
    bgcolor: "primary.main",
    color: "#fff",
    transition: "transform .15s ease, background-color .18s ease",
    "& .MuiButton-endIcon svg": { fontSize: 16 },
    "&:hover": {
      bgcolor: (theme) => theme.palette.primary.hover ?? darken(theme.palette.primary.main, 0.3),
      transform: "translateY(-1px)",
    },
    "&:active": { transform: "translateY(0) scale(.985)" },
    "&.Mui-disabled": { bgcolor: "primary.main", color: "#fff", opacity: 0.4 },
  },
  // Acción secundaria como enlace subrayado (ej. descarga de la tabla de amortización del cotizador).
  link: {
    px: 0,
    py: 0,
    minWidth: 0,
    fontFamily: (theme) => theme.typography.fontFamily,
    fontSize: 13,
    fontWeight: 400,
    // El enlace puede ser largo: se parte en varias líneas en vez de ensanchar la pantalla en móvil.
    whiteSpace: "normal",
    textAlign: "left",
    color: "primary.main",
    textDecoration: "underline",
    textUnderlineOffset: "3px",
    "&:hover": { bgcolor: "transparent", textDecoration: "underline", color: "primary.dark" },
  },
  // Acción chica junto a una cifra (`.fxu-edit` del diseño, ej. "Seleccionar menor monto de línea"): blanco con
  // el borde y los neutros de la marca, 11.5px.
  compact: {
    minWidth: 0,
    px: "9px",
    py: "4px",
    borderRadius: "7px",
    fontFamily: (theme) => theme.typography.fontFamily,
    fontSize: 11.5,
    lineHeight: 1.4,
    bgcolor: "background.paper",
    color: "tokens.textSecondary",
    border: "1px solid",
    borderColor: "divider",
    "& .MuiButton-startIcon": { mr: "5px", ml: 0 },
    "&:hover": { bgcolor: "tokens.surface2", color: "tokens.textPrimary", borderColor: "divider" },
    "&.Mui-disabled": { bgcolor: "background.paper", opacity: 0.4 },
  },
  // `.btn-primary` del prototipo (ej. "Conectar con Didi" en el perfil de conductor de WuWei): navy de la marca
  // (`primary.dark`), más oscuro en hover.
  navy: {
    ...BTN_PROTOTIPO_SX,
    bgcolor: "primary.dark",
    borderColor: "primary.dark",
    color: "#fff",
    "&:hover": {
      ...BTN_PROTOTIPO_SX["&:hover"],
      bgcolor: (theme) => darken(theme.palette.primary.dark, 0.35),
      borderColor: (theme) => darken(theme.palette.primary.dark, 0.35),
    },
    "&.Mui-disabled": { bgcolor: "primary.dark", borderColor: "primary.dark", color: "#fff", opacity: 0.7 },
  },
  // `.btn-secondary` del prototipo (ej. "Conectar con Uber"): blanco con borde gris; en hover, borde y texto del color
  // de la marca con su fondo tenue.
  surface: {
    ...BTN_PROTOTIPO_SX,
    bgcolor: "background.paper",
    borderColor: "tokens.borderStrong",
    color: "tokens.textPrimary",
    boxShadow: "0 1px 2px rgba(14, 31, 58, 0.05)",
    "&:hover": {
      ...BTN_PROTOTIPO_SX["&:hover"],
      bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
      borderColor: "primary.main",
      color: "primary.main",
      boxShadow: (theme) => `inset 0 0 0 1px ${theme.palette.primary.main}, 0 1px 2px rgba(14, 31, 58, 0.05)`,
    },
    "&.Mui-disabled": {
      bgcolor: "background.paper",
      borderColor: "tokens.borderStrong",
      color: "tokens.textPrimary",
      opacity: 0.7,
    },
  },
};

/** Tonos que se pintan como texto (sin fondo de botón). */
const TEXT_TONES: ButtonTone[] = ["ghost", "link"];

export const Button = ({ tone = "primary", withArrow, children, sx, endIcon, ...rest }: Props) => (
  <MuiButton
    variant={TEXT_TONES.includes(tone) ? "text" : "contained"}
    disableElevation
    disableRipple={tone === "link"}
    sx={[
      { borderRadius: "8px", textTransform: "none", fontWeight: 600, px: 3 },
      TONE_SX[tone],
      ...(Array.isArray(sx) ? sx : [sx]),
    ]}
    endIcon={withArrow ? <ArrowForward fontSize="small" /> : endIcon}
    {...rest}
  >
    {children}
  </MuiButton>
);
